import { useState, type ReactNode } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { fetchPage } from './api'
import { displayText, labelKey } from './cellValue'
import { Pager } from './Pager'
import { DEFAULT_PAGE_SIZE, listQueryKey } from './useListState'
import type {
  ColumnDescriptor,
  FilterSpec,
  ListGroup,
  ListPage,
  ListRow,
  ListViewDefinition,
} from './types'

/**
 * Одна группа сгруппированного списка (дизайн: остатки по складу — строка-заголовок,
 * группы сворачиваются).
 *
 * Сервер отдаёт группы **счётчиками**, а не строками (`ListQueryCompiler.compileGroups`):
 * листать иерархию по курсору значило бы держать курсор на каждую группу. Поэтому группа
 * читает свои строки сама — обычной страницей списка с условием «колонка группы = значение
 * группы», — и у неё своя панель страниц. Плоская страница с заголовками по смене значения
 * врала бы: группа, разрезанная границей страницы, показала бы половину строк под
 * заголовком с полным счётчиком.
 *
 * Название группы — то, что сервер кладёт рядом со ссылкой (`<колонка>__label`). Счётчики
 * групп его не несут, поэтому свёрнутая группа по колонке-ссылке спрашивает одну строку
 * ради названия: UUID склада в заголовке не читается никем.
 */
export function GroupRows({
  entity,
  definition,
  column,
  descriptor,
  group,
  expanded,
  onToggle,
  renderRow,
  columnCount,
  enrich,
}: {
  entity: string
  definition: ListViewDefinition
  column: string
  descriptor: ColumnDescriptor | undefined
  group: ListGroup
  expanded: boolean
  onToggle: () => void
  renderRow: (row: ListRow) => ReactNode
  columnCount: number
  enrich: (row: ListRow) => ListRow
}) {
  const { t, locale } = useApp()
  const condition: FilterSpec =
    group.value === null || group.value === undefined
      ? { column, operator: 'is_null' }
      : { column, operator: 'eq', value: String(group.value) }
  const groupDefinition: ListViewDefinition = {
    ...definition,
    filters: [...definition.filters, condition],
    groupBy: null,
  }
  const pageSize = definition.pageSize ?? DEFAULT_PAGE_SIZE
  const [stack, setStack] = useState<(string | null)[]>([null])
  const cursor = stack[stack.length - 1] ?? null

  const page = useQuery<ListPage & { index: number }>({
    queryKey: [...listQueryKey(entity, groupDefinition), cursor],
    enabled: expanded,
    queryFn: ({ signal }) =>
      fetchPage(entity, groupDefinition, cursor, signal).then((data) => ({
        ...data,
        index: stack.length - 1,
      })),
    placeholderData: keepPreviousData,
    retry: false,
  })

  // Название приезжает со строками группы, только если колонка группы среди показанных.
  const needsLabel =
    Boolean(descriptor?.reference) &&
    group.value !== null &&
    !(expanded && definition.columns.includes(column))
  const labelQuery = useQuery({
    queryKey: [...listQueryKey(entity, { ...groupDefinition, columns: [column], pageSize: 1 }), 'label'],
    enabled: needsLabel,
    queryFn: ({ signal }) =>
      fetchPage(entity, { ...groupDefinition, columns: [column], sort: [], pageSize: 1 }, null, signal),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const sample = labelQuery.data?.rows[0] ?? page.data?.rows[0]
  const values: Record<string, unknown> = { [column]: group.value, ...(sample ?? {}) }
  const label =
    group.value === null || group.value === undefined
      ? t('list.groupEmpty')
      : (typeof values[labelKey(column)] === 'string' && values[labelKey(column)] !== ''
          ? String(values[labelKey(column)])
          : displayText(values, column, descriptor, entity, t, locale))

  const rows = (page.data?.rows ?? []).map(enrich)
  const shownIndex = page.data?.index ?? 0
  const from = rows.length === 0 ? 0 : shownIndex * pageSize + 1

  return (
    <>
      <tr className="group">
        <td colSpan={columnCount}>
          <span className="row group-head">
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-icon"
              aria-expanded={expanded}
              aria-label={t(expanded ? 'list.groupCollapse' : 'list.groupExpand', { group: label })}
              onClick={onToggle}
            >
              <Icon name="chevron" size="sm" className={expanded ? undefined : 'is-collapsed'} />
            </button>
            <span>{label}</span>
            <span className="t-sm muted group-meta">
              {t('list.groupCount', { count: group.count.toLocaleString(locale) })}
            </span>
          </span>
        </td>
      </tr>
      {expanded && page.isLoading && (
        <tr>
          <td colSpan={columnCount}>
            <span className="skel" style={{ width: '40%' }} />
          </td>
        </tr>
      )}
      {expanded && page.error && (
        <tr>
          <td colSpan={columnCount} role="alert" className="err-text">
            {t('app.error')}
          </td>
        </tr>
      )}
      {expanded && rows.map(renderRow)}
      {expanded && (stack.length > 1 || Boolean(page.data?.hasMore && page.data.nextCursor)) && (
        <tr className="group-pager">
          <td colSpan={columnCount}>
            <Pager
              from={from}
              to={from === 0 ? 0 : from + rows.length - 1}
              total={group.count}
              hasPrev={stack.length > 1}
              hasNext={Boolean(page.data?.hasMore && page.data.nextCursor)}
              busy={page.isFetching}
              onPrev={() => setStack(stack.slice(0, -1))}
              onNext={() => {
                const next = page.data?.nextCursor
                if (next) setStack([...stack, next])
              }}
              pageSize={pageSize}
            />
          </td>
        </tr>
      )}
    </>
  )
}
